import { CardData } from '@/views/home/customer/plan/LifeRoadPage';

export interface SavingsAdjustment {
  year: number;
  withdrawal?: number;
  savings_rate?: number;
}

export enum MaritalStatus {
  Single = 'single',
  Married = 'married',
  Divorced = 'divorced',
  Widowed = 'widowed',
  Cohabiting = 'cohabiting',
  SingleParent = 'single_parent',
}

export enum LegalForm {
  IndividualEnterprise = 'individual_enterprise',
  LimitedCompany = 'limited_company',
  GBR = 'gbr',
  GeneralPartnership = 'general_partnership',
  KG = 'kg',
  PartG = 'partg',
  SilentPartnership = 'silent_partnership',
  UG = 'ug',
  AG = 'ag',
  GmbHCoKG = 'gmbh_co_kg',
  AGCoKG = 'ag_co_kg',
  EG = 'eg',
  EV = 'ev',
  Other = 'other',
}

export enum YesNo {
  No = 'no',
  Yes = 'yes',
}

export enum PensionInsuranceStatus {
  SubjectStatutory = 'subject_statutory',
  VoluntarilyInsured = 'voluntarily_insured',
  NotLegal = 'not_legal',
}

export enum BBesGSimpleService {
  A03 = 'A 03',
  A04 = 'A 04',
  A05 = 'A 05',
  A06E = 'A 06e',
}

export enum BBesGMiddleService {
  A05M = 'A 05m',
  A06 = 'A 06',
  A07 = 'A 07',
  A08 = 'A 08',
  A09 = 'A 09',
}

export enum BBesGSeniorCivilService {
  A09G = 'A 09g',
  A10 = 'A 10',
  A11 = 'A 11',
  A12 = 'A 12',
  A13 = 'A 13',
}

export enum BBesGHigherService {
  A13H = 'A 13h',
  A14 = 'A 14',
  A15 = 'A 15',
  A16 = 'A 16',
  B01 = 'B 01',
  B02 = 'B 02',
  B03 = 'B 03',
  B04 = 'B 04',
  B05 = 'B 05',
  B06 = 'B 06',
  B07 = 'B 07',
  B08 = 'B 08',
  B09 = 'B 09',
  B10 = 'B 10',
  B11 = 'B 11',
  W01 = 'W 01',
  W02 = 'W 02',
  W03 = 'W 03',
}

export enum SalaryGroup {
  A = 'A',
  B = 'B',
  R = 'R',
  W = 'W',
  C = 'C',
  H = 'H',
}

export enum ExperienceLevel {
  One = 'one',
  Two = 'two',
  Three = 'three',
  Four = 'four',
  Five = 'five',
  Six = 'six',
  Seven = 'seven',
  Eight = 'eight',
  Nine = 'nine',
  Ten = 'ten',
  Eleven = 'eleven',
  Twelve = 'twelve',
}

export enum EmploymentStatus {
  Contributions = 'ss_contributions',
  SelfEmpNoEmp = 'self_emp_no_emp',
  SelfEmpWithEmp = 'self_emp_with_emp',
  EmpMdPartner = 'emp_md_partner',
  MiniJob = 'mini_job',
  JobSeeker = 'job_seeker',
  HouseWifeHusband = 'housewife_husband',
  Disabled = 'disabled',
  OldAgePensioner = 'old_age_pensioner',
  CivilServant = 'civil_servant',
  PublicSectorEmployee = 'public_sector_employee',
}

export enum HealthInsuranceStatus {
  GKV = 'Gesetzlich Krankenversichert',
  PKV = 'Privat Krankenversichert',
}

export enum ChildrenCount {
  None = '0',
  One = '1',
  Two = '2',
  Three = '3',
  Four = '4',
  Five = '5',
  Six = '6',
  Seven = '7',
  Eight = '8',
  Nine = '9',
  Ten = '10',
  Eleven = '11',
  Twelve = '12',
}

export interface Child {
  name: string;
  birthdate: string;
  date_of_birth?: string;
  // true, wenn HubSpot nur ein Geburtsjahr kannte und das Datum auf den 01.01.
  // gesetzt wurde (Entscheid Alex 04.08.2026, E2). Fehlt der Key, ist das
  // Datum ein echtes Volldatum — kein `false` als dritte Aussage.
  birthdate_year_only?: boolean;
  // Kindergeld-Anspruch — steuert die Riester-Kinderzulage. Fehlend =
  // berechtigt (Bestandsdaten wurden ohne das Feld angelegt). Kinder ohne
  // Anspruch bleiben in der Liste, weil sie fuer die Kindererziehungszeiten
  // der gesetzlichen Rente weiter zaehlen.
  child_benefit_eligible?: boolean;
}

export interface VwlPauschaleMtl {
  mandant: number | null;
  partner: number | null;
}

export interface TenAEstg {
  incomeThreshold: number;
  isEligible: boolean;
  ownContribution: number;
  totalAllowance: number;
  chartData?: {
    sum_capital: number;
    sum_allowance: number;
    sum_own_contribution: number;
    sum_threshold: number;
    sum_return: number;
  };
}

export interface Customer {
  id: number;
  user_id: number;
  hubspot_contact_id?: string;
  /**
   * Anrede — geschlossene Liste „Herr"/„Frau" (B2, Alex 19.08.2026).
   * Optional, weil der weit ueberwiegende Bestand sie nicht traegt; sie ist
   * ausdruecklich KEIN Pflichtfeld.
   */
  salutation?: string | null;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  email: string;
  /** Zweite Adresse des Mandanten (HubSpot `weitere_email_adresse`). */
  additional_email?: string | null;
  phone: string;
  address: string;
  postal_code: string;
  city: string;
  // Antrags-Personenfelder (BER-67). Geschlecht = Anrede (`salutation`, oben,
  // B2) — Daniels Branch hatte das Feld ein zweites Mal deklariert (Basis vor
  // B2), beim Merge 11.09.2026 zusammengefuehrt.
  nationality?: string;
  birthplace?: string;
  is_student?: boolean | null;
  // Ausweisdaten (BER-67).
  id_document_type?: string;
  id_document_number?: string;
  id_document_authority?: string;
  id_document_issued_at?: string;
  id_document_valid_until?: string;
  id_document_country?: string;
  // Sensible Grunddaten — unverschluesselt wie die uebrigen Felder
  // (Decision Alex 15.07., Task kunden-grunddaten-sensible-felder.md).
  iban: string;
  tax_id: string;
  social_security_number: string;
  // Bruttojahreseinkommen: Geldfeld (ganze Euro) — backend-seitig Zahl.
  gross_annual_income: number | null;
  company_purpose: string;
  employee_count: string;
  revenue: number;
  profit: number;
  pension_amount: number;
  job_title: string;
  health_insurance_provider: string;
  // Monatsbeitrag der PKV. Eigene Spalte, NICHT
  // health_insurance_contribution: die traegt in Wizard und Kunden-Dialogen
  // zwar auch einen PKV-Euro-Betrag, hat aber ueber das frühere
  // Grunddaten-Feld „Zusatzbeitrag (%)" auch Prozentwerte aufgenommen.
  pkv_monthly_contribution?: number | null;
  gross_salary: number;
  net_salary: number;
  additional_income_type: string | null;
  additional_income_amount: number | null;
  child_benefit_eligible_children: number;
  child_benefit_non_eligible_children: number;
  owns_property: boolean;
  has_building_savings_contract: boolean;
  has_home: boolean;
  is_smoker: boolean;
  highest_education: string;
  /** Leadherkunft — HS-Property `leadherkunft` (B4, Alex 28.08.2026). */
  lead_source?: string | null;
  rent_amount: number;
  created_at: string;
  updated_at: string;
  marital_status: MaritalStatus;
  legal_form: LegalForm;
  pension_insurance_status: PensionInsuranceStatus;
  salary_group: SalaryGroup;
  experience_level: ExperienceLevel;
  employment_status: EmploymentStatus;
  health_insurance_status: HealthInsuranceStatus;
  children: Child[] | Child;
  partner: Customer | null;
  // Partner-Stammdaten als JSONB am Customer (Wizard-Schreibpfad). Keine
  // separate Partner-Customer-Row — die Anzeige in CaseBasicDataTab liest
  // direkt hieraus. Optional, weil aeltere Customer-Reste / Mocks das
  // Feld nicht haben.
  partner_data?: PartnerData | null;
  // D6-Fix (30.07.2026): Partner-RELATION (HS-Migration) in Wizard-Keys —
  // Fallback-Anzeige, solange partner_data NULL ist.
  partner_record_data?: PartnerData | null;
  // Geteilte Wizard-Freitext-Notiz (customers.wizard_notes, Cluster 2 vom
  // 2026-06-10). Optional aus dem gleichen Mock-Kompatibilitaetsgrund.
  wizard_notes?: string | null;
  bav_total_deposit?: number;
  bav_subsidy_in_pension_plan_percent?: number;
  bav_is_vl_contributed_to_pension_plan?: number;
  bav_interest_rate?: number;
  bav_capital?: number;
  savings_calculation?: {
    nettogehalt?: number;
    wunschrente?: number;
    current_age?: number;
    target_age?: number;
    savings_rate?: number;
    roi?: number;
    customer_id?: number;
    fundings_sum?: number;
    dynamic?: number;
    adjustment?: SavingsAdjustment[];
    children?: {
      current_age?: number;
      target_age?: number;
      savings_rate?: number;
      roi?: number;
      customer_id?: number;
      fundings_sum?: number;
      dynamic?: number;
      adjustment?: SavingsAdjustment[];
    };
    diagram?: {
      current_age?: number;
      target_age?: number;
      savings_rate?: number;
      roi?: number;
      customer_id?: number;
      fundings_sum?: number;
      dynamic?: number;
      adjustment?: SavingsAdjustment[];
      is_married?: boolean;
    };
  };
  // @todo(KAN-85): not found in swagger but exists in ui
  height?: string;
  weight?: string;
  settings?: {
    options?: Record<string, string>;
    cards?: Record<string, CardData>;
    hiddenSubsidies?: number[];
    laufzeit?: string;
    adjustedNettogehalt?: number;
    adjustedWunschrente?: number;
    adjustedNettogehaltPartner?: number;
    adjustedWunschrentePartner?: number;
    /**
     * Altschreibweise — nur noch als Rueckfall gelesen, nie geschrieben.
     * Bis 19.10.2026 las die Altersvorsorge diesen Schluessel, schrieb aber
     * `adjustedWunschrentePartner` (`views/home/customer/plan/rentenAnpassung.ts`).
     */
    adjustedWunschrenterPartner?: number;
    klv?: {
      sections?: Array<{
        title: string;
        currentBalance: number | '';
        monthlyContribution: number | '';
        startYear: number | '';
        interestRate: number | '';
        totalInterestRate: number | '';
        duration: number | '';
        finalCapital: number;
      }>;
      activeYear?: number | null;
    };
    comparisonCalculator?: {
      inputs: import('./comparisonCalculator').ComparisonInputs;
      withdrawal: import('./comparisonCalculator').WithdrawalPlanInputs;
    };
    debtFreeCalculator?: {
      inputs: import('./debtFreeCalculator').DebtFreeInputs;
      lastResult?: import('./debtFreeCalculator').DebtFreeSummary;
    };
  };
  additional_info?: Record<string, unknown>;
  living_area: number;
  '10aESTG'?: TenAEstg;
  capital_forming_benefits_allowance: boolean;
  /** Arbeitgeber-Anteil der VWL in Euro pro Monat; leer = unbekannt. */
  capital_forming_benefits_amount?: number | null;
  /**
   * Womit das Backend ohne erfassten AG-Anteil rechnet — je Person, Euro pro
   * Monat, `null` wo kein Anspruch besteht. Vom Backend fertig gerechnet
   * (`AssetBuildingAllowance::pauschaleMonatlich`), hier nur angezeigt: das
   * FE fuehrt KEINE eigene Erwerbsstatus-Klassifikation (Decision #213 E6a).
   */
  vwl_pauschale_mtl?: VwlPauschaleMtl;
  pays_church_tax: boolean;
  use_riester?: boolean | null;
  custom_funding?: CustomFunding[];
  school_years_after_17?: number;
  next_meeting_date?: string | null;
  assigned_user_id?: number | null;
  team_id?: number | null;
  crm_status?: string | null;
  recycle_count?: number;
  source?: string | null;
  last_activity_at?: string | null;
}

export type CustomerFormType = 'partner' | 'customer';

// Wizard-Form fuer partner_data (JSONB am Customer). Alle Felder optional,
// weil aeltere Buchungen einige Keys nicht gefuellt haben (z.B. fehlte
// einkuenfte bis zum Phase-4-Partner-Fix).
export interface PartnerData {
  anrede?: string;
  vorname?: string;
  nachname?: string;
  // Volles Geburtsdatum (Y-m-d) — der fuehrende Wert seit 03.08.2026.
  geburtsdatum?: string;
  // Jahrgang. BLEIBT bestehen und wird beim Speichern des Geburtsdatums
  // mitgefuehrt: der Telefonie-Wizard prefillt daraus `geburtsjahrPartner`
  // und der HubSpot-Export schreibt `geburtsjahr__partner_`. Beide erwarten
  // eine vierstellige Jahreszahl.
  geburtsjahr?: number | null;
  berufsstand?: string;
  berufsbezeichnung?: string;
  bruttogehalt?: number | null;
  nettogehalt?: number | null;
  krankenkasse?: string;
  einkuenfte?: string;
  einkuenfteHoehe?: number | null;
  // Wizard-Slug 'ja'/'nein' (Cluster 3 vom 2026-06-10). HS-Mapping
  // `HS_KIRCHENSTEUER_PARTNER` liest aus diesem JSONB-Sub-Key.
  kirchensteuer?: string;
  // Foerderrelevante Partner-Angaben. Auf main hatte der Partner sie, weil er
  // ein vollwertiger Mandanten-Datensatz war; beim Umzug nach partner_data
  // (#69) sind sie verlorengegangen, weil das JSONB nach dem Fragenkatalog des
  // Telefonie-Wizards geformt wurde und der den Partner danach nicht fragt.
  //
  // Wizard-Slugs 'ja'/'nein' wie kirchensteuer — bewusst KEINE Booleans,
  // damit sie sich im JSONB wie die uebrigen Ja/Nein-Keys lesen.
  // Sie gehoeren NICHT zu den HubSpot-Partner-Keys: HubSpot fuehrt sie nicht.
  vermoegenswirksameLeistungen?: string;
  /** Arbeitgeber-Anteil der VWL in Euro pro Monat; leer = unbekannt (wie beim Mandanten). */
  vermoegenswirksameLeistungenBetrag?: number | null;
  riester?: string;
  schuljahre?: number | null;
  bausparvertrag?: string;
  // Personenbezogene Angaben, die der Partner bisher nicht fuehren konnte.
  // Alle rein erfassend — keine davon geht in eine Foerderrechnung ein.
  // BW-intern, nicht Teil der HubSpot-Partner-Keys.
  familienstand?: string;
  hoechsterAbschluss?: string;
  kindergeldberechtigteKinder?: number | null;
  email?: string;
  telefon?: string;
  rentenanspruch?: number | null;
  // Adresse: der Partner wohnt normalerweise beim Mandanten. Erst wenn
  // `abweichendeAdresse` auf 'ja' steht, werden eigene Felder erfasst.
  abweichendeAdresse?: string;
  adresse?: string;
  plz?: string;
  ort?: string;
  // Krankenversicherung des Partners. Keine Rechenklasse liest sie — reine
  // Erfassung, wie beim Mandanten auch. Der KV-Typ steuert nur, ob die
  // Kassenliste geladen wird und ob der PKV-Beitrag erscheint.
  kvTyp?: string;
  pkvBeitrag?: number | null;
}

export type CustomerForm = Record<CustomerFormType, Partial<Customer> | null>;

export interface CustomerFormProps {
  formKey: CustomerFormType;
}

export interface Insurance {
  category: 'property_liability' | 'life_financial';
  type: string;
  new_type: string;
  provider: string;
  monthly_payment: number;
  annual_payment: number;
  new_provider: string;
  new_monthly_payment: number;
  new_annual_payment: number;
  info: string;
  roi: number;
  new_roi: number;
  expense: number;
  start_year: string;
  duration_in_years: number;
  new_duration_in_years: number;
  credit_balance: number;
  new_credit_balance: number;
  future_values: number[];
  id: number;
  payment_frequency?: string;
  new_payment_frequency?: string;
  action?: string;
}

export interface Funding {
  comment: string;
  description: string;
  id: number;
  // Stabiler Schlüssel der Zeile (Backend-Rechenklasse, z. B. `FundingLaw`).
  // Optional, weil ein Backend vor dem 19.10.2026 ihn nicht liefert.
  name?: string;
  purpose: string;
  sum: number;
  sum_total?: number;
  type: string;
}

export type CustomFundingType = 'bar' | 'nachgelagert' | 'immobilien' | 'steuer';

export interface CustomFunding {
  id?: string;
  name: string;
  purpose: string;
  amount: number;
  total?: number;
  // Freitext-Förderhöhe (Steuer- und seit 19.10.2026 auch Immobilien-Sektion)
  // — z. B. "20%". Bewusst getrennt
  // von `total` (numerisch, für Summen + Backend-numeric-Validierung).
  foerderhoehe?: string;
  type?: CustomFundingType;
}

export interface CalculateSavingsRequest {
  current_age: number;
  savings_rate: number;
  target_age: number;
  roi: number;
  fundings_sum: number;
  dynamic?: number;
  is_married?: boolean;
  adjustment?: SavingsAdjustment[];
}

export interface CalculateSavingsResponse {
  year: number;
  age: number;
  monthly: number;
  savings_rate: number;
  cash_subsidy: number;
  annual_payment: number;
  annual_sum: number;
  withdrawal: number;
  payment_total: number;
  capital: number;
  final_capital: number;
  hev: number;
  subsidy_tax_free: number;
  /**
   * Nur an der Zeile Zielalter − 1 (Stand bei Rentenbeginn), sonst null:
   * steuerfreier Anteil, geprueft mit Auszahlungsalter = Zielalter und
   * Laufzeit bis zum Zielalter (Entscheid Uenal 20.10.2026). Die Spalte
   * `subsidy_tax_free` je Zeile prueft das Alter der Zeile.
   */
  subsidy_tax_free_at_payout: number | null;
  return_of_investment: number;
  sum_withdrawal: number;
  sum_adjustment: number;
  cost: number;
}

/** Konzeptprodukt, wie die API es liefert. */
export interface ConceptProduct {
  id: number;
  type: string;
  provider: string | null;
  monthly_payment: number | null;
  lump_sum_payment: number | null;
  funding_immediate: number | null;
  funding_future: number | null;
  funding_future_custom: string | null;
}

/** Nutzlast für POST/PUT — PUT ersetzt die ganze Zeile, `null` leert ein Feld. */
export type ConceptProductPayload = Omit<ConceptProduct, 'id' | 'funding_future'>;

/**
 * Eine Zeile der Konzepttabelle (Formik). Geldfelder kommen aus Textfeldern
 * und können deshalb Strings sein.
 */
export interface ConceptProductRow {
  id?: number;
  /** Stabiler React-Key — eine neue Zeile hat noch keine `id`. */
  __key?: string;
  product?: string;
  custom_product_name?: string;
  provider?: string | null;
  monthly_payment?: number | string | null;
  lump_sum_payment?: number | string | null;
  funding_immediate?: number | string | null;
  funding_future?: number | null;
  funding_future_custom?: string | null;
}

export interface DocumentsTable {
  id: number;
  type: string | null;
  requirement_level: string;
  provider: string;
  contribution: number;
  comment: string | null;
  // F17 Upload-Felder (nullable, da nur bei file uploads gesetzt)
  original_name?: string | null;
  mime_type?: string | null;
  file_size?: number | null;
  // Herkunft (Decision #126): 'customer' = vom Kunden ueber den Upload-Link.
  source?: string | null;
  uploaded_at?: string | null;
  download_url?: string | null;
  // Ordner/Kategorie (FB 79, B3) — null = unsortiert ("Allgemein").
  folder?: string | null;
  // Nachweis-Zuordnung (BER-67): grün-Logik im Antrags-Wizard.
  // 'ausweis' | 'adressnachweis' | 'gehaltsnachweis' | null.
  nachweis_typ?: string | null;
  // Synthetischer Wizard-Personen-Schlüssel (customer/partner/child:N) — nur
  // beim Ausweis gesetzt (pro-Person-Zuordnung).
  person_ref?: string | null;
}

export type InsuranceCreate = Omit<Insurance, 'future_values'>;

export interface HealthInsurance {
  id: number;
  name: string;
  additional_contribution: number;
  is_open: boolean;
  region: string | null;
}

export interface HealthInsuranceComparisonAlternative {
  id: number;
  name: string;
  additional_contribution: number;
  employee_rate: number;
  monthly_cost: number;
  savings_monthly: number;
  savings_annual: number;
}

export interface HealthInsuranceComparisonResult {
  current: {
    name: string;
    additional_contribution: number;
    employee_rate: number;
    monthly_cost: number;
    annual_cost: number;
  };
  assessment_basis: number;
  general_contribution_rate: number;
  contribution_ceiling_monthly: number;
  alternatives: HealthInsuranceComparisonAlternative[];
  cheapest: HealthInsuranceComparisonAlternative | null;
  max_savings_monthly: number;
  max_savings_annual: number;
}

/**
 * HS-Anzeigefelder eines Kunden — LIVE aus HubSpot nachgeschlagen, in BW NICHT
 * gespeichert (docs/tasks/hs-heatedup-anzeige.md).
 *
 * `value` ist FREITEXT aus der HS-Property `heatedup_` und bewusst `string`,
 * kein Union-Typ: die real vorkommenden Auspraegungen („Positiv", „Nicht
 * erreicht nach 18 Uhr", …) sind eine Gewohnheit des Callcenters, keine
 * Optionsliste. Ein Union haette hier nur die Wirkung, dass der naechste neue
 * Wert einen Typfehler ausloest statt angezeigt zu werden.
 *
 * `available` trennt zwei Zustaende, die sonst beide wie „kein Wert" aussaehen:
 *   - `true`  + `value: null` → nachgeschaut, HubSpot fuehrt nichts.
 *   - `false` + `value: null` → nicht nachschaubar (HS-Stoerung/Timeout).
 */
export interface HubspotFlags {
  value: string | null;
  available: boolean;
}
